import type { ReactNode } from 'react';
import { useAgentChat } from '../agent/useAgentChat';
import { ChatTimeline } from '../agent/ChatTimeline';
import { ChatInput } from '../agent/ChatInput';
import { SuggestionChips } from '../agent/SuggestionChips';
import { useStickToBottom } from '../agent/useStickToBottom';
import { useCreateChatSession } from '../../api/hooks';
import type { Chip } from '../agent/buildSuggestions';
import { darkTheme, FONT } from '../../theme/tokens';

const t = darkTheme;

// SC-2379 — the agent conversation co-located with the table. It's a focused
// ask-about-this-view surface (NOT the home briefing experience), built from the
// reusable chat pieces and `useAgentChat`, so it shares the SAME per-catalog
// conversation thread as the standalone AgentTab (a message here also shows there,
// and vice versa). The starters show off the agent DRIVING the table — the
// directive applies to the canonical TableQuery via useAgentChat's bridge.
const TABLE_STARTERS: readonly Chip[] = [
  { label: 'Show products with issues', prompt: 'Show me the products that have issues.' },
  { label: 'Lowest completeness', prompt: 'Which products have the lowest completeness?' },
  { label: 'Explain this view', prompt: 'Explain what I’m looking at in this table.' },
];

interface ChatConversationProps {
  catalogId: string;
  /** A control docked at the header's far LEFT, before the EKOM wordmark — kept
   *  clear of the New-chat cluster on the right so the two don't read as one
   *  action. The `ChatPanel` puts its Collapse control here. */
  leading?: ReactNode;
  /** The dismiss control for the header's top-RIGHT, after the shared "New chat"
   *  action — the fixed dock's Close ✕ (a right-edge control by convention). */
  trailing?: ReactNode;
}

/**
 * The chat body of the workspace's permanent `ChatPanel` (extracted at P3/SC-2659).
 * Owns the whole conversation — header (EKOM label + New chat + the host's dismiss
 * control), the stick-to-bottom timeline / empty-state starters / error alert, and
 * the input — so the host only supplies the outer chrome (positioning, resize
 * geometry, collapse). Fills its parent as a flex column; the host provides the
 * sized, positioned container.
 */
export function ChatConversation({ catalogId, leading, trailing }: ChatConversationProps) {
  const chat = useAgentChat(catalogId);
  const createSession = useCreateChatSession(catalogId);
  const sendDisabled = chat.status === 'streaming';
  const handleSend = (text: string) => void chat.send(text);

  const turns = chat.replayQuery.data?.turns ?? [];
  const hasContent =
    turns.length > 0 ||
    chat.localGroups.length > 0 ||
    chat.pendingUserText !== null ||
    chat.inFlight !== null;
  // New chat: same wiring as AgentTab. Disabled mid-stream, while the create is
  // in flight, and when there are no turns yet (the BE reuses an empty session, so
  // it'd be a no-op).
  const newChatDisabled = sendDisabled || createSession.isPending || !hasContent;
  const newChatTitle = sendDisabled
    ? 'Wait for the response to finish'
    : createSession.isPending
      ? 'Creating new session…'
      : !hasContent
        ? 'Send a message first'
        : 'Start new chat';
  const scrollRef = useStickToBottom<HTMLDivElement>([
    turns.length,
    chat.localGroups.length,
    chat.pendingUserText,
    chat.inFlight,
    chat.inFlight?.items.length ?? 0,
  ]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '14px 16px',
          borderBottom: `1px solid ${t.border}`,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {leading}
          <span
            style={{
              fontSize: 12,
              fontWeight: 600,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: t.text3,
            }}
          >
            EKOM
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button
            type="button"
            onClick={() => createSession.mutate()}
            disabled={newChatDisabled}
            title={newChatTitle}
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              color: newChatDisabled ? t.text3 : t.blueText,
              fontSize: 12,
              fontWeight: 500,
              fontFamily: FONT,
              cursor: newChatDisabled ? 'default' : 'pointer',
            }}
          >
            New chat
          </button>
          {trailing}
        </div>
      </div>

      <div ref={scrollRef} style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: 16 }}>
        {hasContent ? (
          <ChatTimeline
            turns={turns}
            localGroups={chat.localGroups}
            pendingUserText={chat.pendingUserText}
            inFlight={chat.inFlight}
            isStreaming={chat.status === 'streaming'}
          />
        ) : (
          <div>
            <p style={{ color: t.text3, fontSize: 13, margin: '0 0 12px' }}>
              Ask me to filter, find, or explain this table — I’ll update the view.
            </p>
            <SuggestionChips chips={TABLE_STARTERS} onPick={handleSend} disabled={sendDisabled} />
          </div>
        )}
        {chat.error && (
          <div
            role="alert"
            style={{
              color: t.redText,
              fontSize: 13,
              marginTop: 12,
              padding: '8px 12px',
              background: t.redBg,
              border: `1px solid ${t.red}`,
              borderRadius: 8,
            }}
          >
            {chat.error}
          </div>
        )}
      </div>

      <div style={{ padding: '12px 16px', borderTop: `1px solid ${t.border}` }}>
        <ChatInput onSend={handleSend} disabled={sendDisabled} />
      </div>
    </div>
  );
}
